// Client-side preparation of a refill-sheet upload (group-home roster refills, PR-E):
// a phone photo is made small enough for the server's per-image cap BEFORE it is sent,
// upright, without losing the handwriting.
//
// The numbers are the server's, not guesses. The CAPS come from the server itself:
// GET /api/sms/chat/roster/facilities answers `limits` — per type, the bytes one page
// image may be, and the total for every page — built from the very options the upload is
// validated with (routes/orderChatRoster.js rosterUploadLimits). DEFAULT_LIMITS below is
// only the fallback for an answer without them (the dialog prepares nothing before the
// served limits answer), and mirrors the server with #748 in it:
//   IMAGE_MAX_BYTES   uploadLimits.js IMAGE_MAX_BYTES — 4.5 MB per JPEG/PNG page (the
//                     margin under Bedrock's 5 MB image cap); a bigger page is refused 413.
//   HEIC_MAX_BYTES    uploadLimits.js HEIC_MAX_BYTES (#748, PR-C2, OD-1) — 12 MB for a page
//                     whose bytes sniff as HEIC; served in `limits` as image/heic and
//                     image/heif. A server without #748 serves no HEIC entry, and a HEIC is
//                     then refused here before it is sent.
//   TARGET_LONG_EDGE  orderChatRoster/pages.js MAX_LONG_EDGE — 2,200 px, a letter page at
//                     ~200 dpi. The server holds every page to it anyway, so a photo sent
//                     at exactly this edge, upright (orientation 1) and under the cap is
//                     used AS ITSELF (pages.js `asItself`): one JPEG encode, never two.
//   DOC_MAX_BYTES     uploadLimits.js DOC_MAX_BYTES — 15 MB for a PDF, sent as-is, and
//                     15 MB for every page image of one upload together.
//   MAX_PAGES         orderChatRoster/pages.js ROSTER_MAX_PAGES — 4 page images.
//
// EXIF orientation. A phone photo is stored sideways with an orientation tag. Every
// browser this app supports applies the tag when it decodes (createImageBitmap with
// imageOrientation 'from-image', and <img> by default), so the canvas receives the
// upright pixels and the JPEG it writes carries no EXIF at all — upright for the server
// and the model alike. A decoder that IGNORED the tag is caught where it matters (5–8,
// the quarter turns: an iPhone portrait is 6) by its size — the decoded image is the
// stored width × height instead of the swapped one — and the turn is drawn here instead.
//
// HEIC. The file input never lists image/heic (O169), so iOS Safari hands over a JPEG.
// A HEIC that arrives anyway (drag-drop or paste from a Mac) is decoded and re-encoded
// when the browser can (Safari); when it cannot, its bytes go up as image/heic for the
// server to decode — only when the server's `limits` take HEIC (#748), and only up to its
// HEIC cap. Otherwise the screen says so before anything is sent.
//
// Pure planning + a small DOM adapter (`domDeps`), so a test drives the planning and the
// whole prepare with injected decode/encode. CommonJS, like lib/order-batch.js.

const IMAGE_MAX_BYTES = Math.floor(4.5 * 1024 * 1024);
// #748 (PR-C2, OD-1's recommended arm): uploadLimits.HEIC_MAX_BYTES — a page whose bytes
// sniff as HEIC may be 12 MB on the roster upload (a phone's "HEIF Max" photo; this page
// can only resize a HEIC in a browser that draws one). The served `limits` carry it; this
// constant is the same number for the fallback below (pinned to the server's by
// image-resize.test.js).
const HEIC_MAX_BYTES = 12 * 1024 * 1024;
const DOC_MAX_BYTES = 15 * 1024 * 1024;
const TARGET_LONG_EDGE = 2200;
const MAX_PAGES = 4;
// The server's caps when its `limits` did not arrive: the roster validator with #748 in
// it (JPEG/PNG at 4.5 MB each, a sniffed HEIC at 12 MB, 15 MB in all, 4 pages). The dialog
// prepares nothing before the served limits answer, so this is only for an answer
// without them.
const DEFAULT_LIMITS = Object.freeze({
  maxPages: MAX_PAGES,
  docMaxBytes: DOC_MAX_BYTES,
  imageMaxBytes: Object.freeze({ 'image/jpeg': IMAGE_MAX_BYTES, 'image/png': IMAGE_MAX_BYTES, 'image/heic': HEIC_MAX_BYTES }),
});
const MB = (n) => `${Math.round((n / (1024 * 1024)) * 10) / 10} MB`;

// The server's limits as given, each field checked; anything missing is the fallback's.
function normalizeLimits(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const pos = (v, d) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : d);
  const images = r.imageMaxBytes && typeof r.imageMaxBytes === 'object' ? r.imageMaxBytes : DEFAULT_LIMITS.imageMaxBytes;
  const imageMaxBytes = {};
  for (const [mime, n] of Object.entries(images)) {
    const v = pos(n, 0);
    if (v) imageMaxBytes[String(mime).toLowerCase()] = v;
  }
  if (!Object.keys(imageMaxBytes).length) Object.assign(imageMaxBytes, DEFAULT_LIMITS.imageMaxBytes);
  return {
    maxPages: Math.floor(pos(r.maxPages, DEFAULT_LIMITS.maxPages)),
    docMaxBytes: pos(r.docMaxBytes, DEFAULT_LIMITS.docMaxBytes),
    imageMaxBytes,
  };
}
// One page's cap for its type (image/heif is the server's image/heic); 0 = not taken.
function imageCapFor(limits, type) {
  const l = limits || DEFAULT_LIMITS;
  const t = type === 'image/heif' ? 'image/heic' : type;
  return (l.imageMaxBytes && (l.imageMaxBytes[t] || (t === 'image/heic' && l.imageMaxBytes['image/heif']))) || 0;
}
// JPEG quality, best first: 0.85 keeps pen strokes crisp at 2,200 px; the lower rungs
// are only for a photo that is still over the cap (a very noisy one).
const QUALITY_LADDER = Object.freeze([0.85, 0.78, 0.7]);
// …and if that is not enough, a smaller edge — never below this, where a ballpoint
// strength ("0.5 mg") starts to blur. Past it the photo is refused, not mangled.
const FALLBACK_EDGES = Object.freeze([1900, 1600]);

// ── What the bytes are: the server's own rules, exactly ──────────────────────────
//
// uploadLimits.sniffImageType (PNG's 8-byte magic, JPEG's ffd8ff, then HEIF) and isPdf
// ('%PDF-'), and for HEIF #748's pageGate/heifHeader `sniffHeif` — so a file this page
// sends as image/heic is never the server's 400 "not the image type it says it is":
//   * the FIRST box is `ftyp` with an 8-byte header (a 64-bit size is not taken), inside
//     the file, 16..4,096 bytes, a whole number of 4-byte brands after the minor version;
//   * an AVIF major brand (avif, avis) is never HEIF;
//   * HEIF = a HEIF MAJOR brand, or a HEVC brand among the compatible ones. mif1/msf1
//     count only as the MAJOR brand (#748 R4: `isom` + compatible mif1 is an MP4, not a
//     HEIC); there is no `hevx`.
// The lists are the server's, pinned against heifHeader's own exports by
// image-resize.test.js (every vector through both sniffs, the lists equal), so a brand
// that moves on one side fails the build. They are copied, not imported: nothing in the
// v3 bundle imports from server/.
const HEIF_MAJOR_BRANDS = Object.freeze(['heic', 'heix', 'hevc', 'heim', 'heis', 'mif1', 'msf1']);
const HEVC_BRANDS = Object.freeze(['heic', 'heix', 'hevc', 'heim', 'heis']);
const AV1_BRANDS = Object.freeze(['avif', 'avis']);
const FTYP_MAX_BYTES = 4096;

function ascii(bytes, start, len) {
  let s = '';
  for (let i = start; i < start + len && i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]);
  return s;
}

// The file's ftyp box as heifHeader.readFtyp reads it: { major, compatible } or null.
function readFtyp(bytes) {
  if (!bytes || bytes.length < 16) return null;
  if (ascii(bytes, 4, 4) !== 'ftyp') return null;
  const declared = (bytes[0] << 24 | bytes[1] << 16 | bytes[2] << 8 | bytes[3]) >>> 0;
  const size = declared === 0 ? bytes.length : declared;   // 0 = to the end of the file
  // inside the file; 1 (a 64-bit size, header 16 — the server takes only an 8-byte header)
  // and anything under 8 are refused here too
  if (size < 8 || size > bytes.length) return null;
  if (size < 16 || size > FTYP_MAX_BYTES || (size - 16) % 4 !== 0) return null;
  const major = ascii(bytes, 8, 4);
  const compatible = [];
  for (let at = 16; at < size; at += 4) compatible.push(ascii(bytes, at, 4));
  return { major, compatible };
}

function sniffHeif(bytes) {
  const f = readFtyp(bytes);
  if (!f || AV1_BRANDS.includes(f.major)) return false;
  return HEIF_MAJOR_BRANDS.includes(f.major) || f.compatible.some((x) => HEVC_BRANDS.includes(x));
}

const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

// What the BYTES are — the declared type of a dropped or pasted file is not trusted (the
// server sniffs too, and refuses a mismatch).
function sniffType(bytes) {
  if (!bytes || bytes.length < 5) return null;
  if (ascii(bytes, 0, 5) === '%PDF-') return 'application/pdf';
  if (bytes.length >= 8) {
    if (PNG_MAGIC.every((v, i) => bytes[i] === v)) return 'image/png';
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  }
  if (sniffHeif(bytes)) return 'image/heic';
  return null;
}

function u16(bytes, i, little) {
  return little ? (bytes[i] | bytes[i + 1] << 8) : (bytes[i] << 8 | bytes[i + 1]);
}
function u32(bytes, i, little) {
  return little
    ? ((bytes[i] | bytes[i + 1] << 8 | bytes[i + 2] << 16 | bytes[i + 3] << 24) >>> 0)
    : ((bytes[i] << 24 | bytes[i + 1] << 16 | bytes[i + 2] << 8 | bytes[i + 3]) >>> 0);
}

// The orientation tag (0x0112) of IFD0 inside an APP1 Exif segment starting at `start`
// (the "Exif\0\0" header). 1 when absent or unreadable.
function exifOrientation(bytes, start, end) {
  const tiff = start + 6;
  if (tiff + 8 > end) return 1;
  const order = ascii(bytes, tiff, 2);
  if (order !== 'II' && order !== 'MM') return 1;
  const little = order === 'II';
  if (u16(bytes, tiff + 2, little) !== 42) return 1;
  const ifd = tiff + u32(bytes, tiff + 4, little);
  if (ifd + 2 > end) return 1;
  const count = u16(bytes, ifd, little);
  for (let k = 0; k < count; k += 1) {
    const e = ifd + 2 + k * 12;
    if (e + 12 > end) break;
    if (u16(bytes, e, little) === 0x0112) {
      const v = u16(bytes, e + 8, little);
      return v >= 1 && v <= 8 ? v : 1;
    }
  }
  return 1;
}

const SOF_MARKERS = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);

// A JPEG's STORED size (before orientation) and its orientation tag, from the header
// alone. null when the bytes are not a readable JPEG header.
function readJpegInfo(bytes) {
  if (sniffType(bytes) !== 'image/jpeg') return null;
  let orientation = 1;
  let i = 2;
  while (i + 4 <= bytes.length) {
    if (bytes[i] !== 0xff) return null;
    const marker = bytes[i + 1];
    if (marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) { i += 2; continue; }
    if (marker === 0xff) { i += 1; continue; }
    const len = u16(bytes, i + 2, false);
    if (len < 2) return null;
    const segStart = i + 4;
    const segEnd = Math.min(i + 2 + len, bytes.length);
    if (marker === 0xe1 && ascii(bytes, segStart, 6) === 'Exif\u0000\u0000') {
      orientation = exifOrientation(bytes, segStart, segEnd);
    } else if (SOF_MARKERS.has(marker)) {
      if (segStart + 5 > bytes.length) return null;
      const height = u16(bytes, segStart + 1, false);
      const width = u16(bytes, segStart + 3, false);
      return width && height ? { width, height, orientation } : null;
    } else if (marker === 0xda) {
      return null; // scan data before any frame header
    }
    i += 2 + len;
  }
  return null;
}

function readPngInfo(bytes) {
  if (sniffType(bytes) !== 'image/png' || bytes.length < 24 || ascii(bytes, 12, 4) !== 'IHDR') return null;
  const width = u32(bytes, 16, false);
  const height = u32(bytes, 20, false);
  return width && height ? { width, height, orientation: 1 } : null;
}

// The size an orientation turns a stored width × height into.
function orientedSize(width, height, orientation) {
  return orientation >= 5 && orientation <= 8 ? { width: height, height: width } : { width, height };
}

// Scale to fit a long edge, never up.
function fitLongEdge(width, height, maxEdge) {
  const long = Math.max(width, height);
  if (!(long > maxEdge)) return { width, height };
  const s = maxEdge / long;
  return { width: Math.max(1, Math.round(width * s)), height: Math.max(1, Math.round(height * s)) };
}

// What to do with one page image. As-is only when the server would use it as itself:
// JPEG or PNG, upright, inside the edge and the cap. Anything else is re-encoded as an
// upright JPEG at the target edge.
function planImage({ type, byteLength, width, height, orientation = 1 }, limits = DEFAULT_LIMITS) {
  const known = type === 'image/jpeg' || type === 'image/png';
  const cap = imageCapFor(limits, type);
  if (known && cap && orientation === 1 && width && height
      && Math.max(width, height) <= TARGET_LONG_EDGE && byteLength <= cap) {
    return { action: 'as_is' };
  }
  const upright = width && height ? orientedSize(width, height, orientation) : null;
  return { action: 'reencode', target: upright ? fitLongEdge(upright.width, upright.height, TARGET_LONG_EDGE) : null };
}

// The canvas transform that draws a STORED image (sw × sh, the size drawImage is given)
// upright onto a canvas of the oriented size — only for a decoder that ignored the tag.
// [a, b, c, d, e, f] for ctx.setTransform.
function orientationTransform(orientation, sw, sh) {
  switch (orientation) {
    case 2: return [-1, 0, 0, 1, sw, 0];
    case 3: return [-1, 0, 0, -1, sw, sh];
    case 4: return [1, 0, 0, -1, 0, sh];
    case 5: return [0, 1, 1, 0, 0, 0];
    case 6: return [0, 1, -1, 0, sh, 0];
    case 7: return [0, -1, -1, 0, sh, sw];
    case 8: return [0, -1, 1, 0, 0, sw];
    default: return [1, 0, 0, 1, 0, 0];
  }
}

// Did the decoder leave a quarter-turn tag unapplied? Its output is then the STORED size.
function decoderIgnoredQuarterTurn(info, decoded) {
  return !!info && info.orientation >= 5 && info.width !== info.height
    && decoded.width === info.width && decoded.height === info.height;
}

class PrepareError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'PrepareError';
    this.code = code;
  }
}

const PREPARE_TEXT = Object.freeze({
  unreadable: "This file couldn't be opened as a photo, a scan or a PDF.",
  too_large: 'This photo is too detailed to send, even made smaller. Take it again a little further from the sheet, or scan it to a PDF.',
  pdf_too_large: (max) => `This PDF is too large (${MB(max)} at most). Scan it at a lower resolution, or send photos of the pages.`,
  heic_unsupported: "This browser can't open this iPhone (HEIC) photo, and it can't be sent as one yet. Export it as a JPEG, or take the photo again, and add it here.",
  heic_too_large: (max) => `This iPhone (HEIC) photo is too large (${MB(max)} at most per photo). Take it again, or export it as a JPEG.`,
});

// One file → { mimeType, bytes, kind, resized, width?, height? }, ready to base64.
// `deps`: { readBytes(file) → ArrayBuffer, decode(file) → { width, height, source, close? },
//          encode(decoded, { width, height, transform }, quality) → ArrayBuffer }.
async function prepareFile(file, deps, rawLimits = null) {
  const limits = normalizeLimits(rawLimits);
  const bytes = new Uint8Array(await deps.readBytes(file));
  const type = sniffType(bytes);
  if (type === 'application/pdf') {
    if (bytes.length > limits.docMaxBytes) throw new PrepareError('pdf_too_large', PREPARE_TEXT.pdf_too_large(limits.docMaxBytes));
    return { kind: 'pdf', mimeType: type, bytes, resized: false };
  }
  const jpegCap = imageCapFor(limits, 'image/jpeg') || IMAGE_MAX_BYTES;
  const info = type === 'image/jpeg' ? readJpegInfo(bytes) : type === 'image/png' ? readPngInfo(bytes) : null;
  if (info) {
    const plan = planImage({ type, byteLength: bytes.length, ...info }, limits);
    if (plan.action === 'as_is') {
      return { kind: 'image', mimeType: type, bytes, resized: false, width: info.width, height: info.height };
    }
  }
  let decoded;
  try {
    decoded = await deps.decode(file);
  } catch (_err) {
    decoded = null;
  }
  if (!decoded || !decoded.width || !decoded.height) {
    // A HEIC this browser cannot draw: its own bytes, for the server to decode (#748) —
    // only when the server's limits take HEIC, and only within its HEIC cap.
    if (type === 'image/heic') {
      const heicCap = imageCapFor(limits, 'image/heic');
      if (!heicCap) throw new PrepareError('heic_unsupported', PREPARE_TEXT.heic_unsupported);
      if (bytes.length > heicCap) throw new PrepareError('heic_too_large', PREPARE_TEXT.heic_too_large(heicCap));
      return { kind: 'image', mimeType: 'image/heic', bytes, resized: false };
    }
    throw new PrepareError('unreadable', PREPARE_TEXT.unreadable);
  }
  try {
    const turn = decoderIgnoredQuarterTurn(info, decoded);
    const upright = turn ? orientedSize(decoded.width, decoded.height, info.orientation) : { width: decoded.width, height: decoded.height };
    for (const edge of [TARGET_LONG_EDGE, ...FALLBACK_EDGES]) {
      const target = fitLongEdge(upright.width, upright.height, edge);
      // The stored-size box drawImage fills before the transform turns it.
      const drawn = turn ? { width: target.height, height: target.width } : target;
      const transform = turn ? orientationTransform(info.orientation, drawn.width, drawn.height) : null;
      for (const q of QUALITY_LADDER) {
        const out = new Uint8Array(await deps.encode(decoded, { ...target, drawn, transform }, q));
        if (out.length && out.length <= jpegCap) {
          return { kind: 'image', mimeType: 'image/jpeg', bytes: out, resized: true, width: target.width, height: target.height, quality: q };
        }
      }
    }
    throw new PrepareError('too_large', PREPARE_TEXT.too_large);
  } finally {
    if (decoded && typeof decoded.close === 'function') decoded.close();
  }
}

// Bytes → base64, in chunks (a 4 MB page would overflow a single apply()).
function bytesToBase64(bytes) {
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

// The browser's own decoder and canvas. Not exercised by the unit tests (no DOM there);
// browser QA covers it (O168).
function domDeps() {
  return {
    readBytes: (file) => file.arrayBuffer(),
    async decode(file) {
      if (typeof createImageBitmap === 'function') {
        try {
          const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
          return { width: bmp.width, height: bmp.height, source: bmp, close: () => bmp.close && bmp.close() };
        } catch (_err) { /* fall through to <img> */ }
      }
      const url = URL.createObjectURL(file);
      try {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
        await img.decode();
        return { width: img.naturalWidth, height: img.naturalHeight, source: img };
      } finally {
        URL.revokeObjectURL(url);
      }
    },
    async encode(decoded, { width, height, drawn, transform }, quality) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      // White under a transparent PNG: JPEG has no alpha, and black ink on black is lost.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      if (transform) ctx.setTransform(...transform);
      ctx.drawImage(decoded.source, 0, 0, drawn.width, drawn.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      canvas.width = 0; canvas.height = 0; // free the backing store now
      return blob ? blob.arrayBuffer() : new ArrayBuffer(0);
    },
  };
}

module.exports = {
  IMAGE_MAX_BYTES, DOC_MAX_BYTES, TARGET_LONG_EDGE, MAX_PAGES, QUALITY_LADDER, FALLBACK_EDGES,
  DEFAULT_LIMITS, normalizeLimits, imageCapFor, HEIC_MAX_BYTES,
  HEIF_MAJOR_BRANDS, HEVC_BRANDS, AV1_BRANDS, FTYP_MAX_BYTES, readFtyp, sniffHeif,
  sniffType, readJpegInfo, readPngInfo, orientedSize, fitLongEdge, planImage, orientationTransform,
  decoderIgnoredQuarterTurn, prepareFile, bytesToBase64, domDeps, PrepareError, PREPARE_TEXT,
};
